import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import 'moment-timezone';
import { reject } from 'ramda';
import { useScrollTop } from '@alycecom/hooks';

import RecipientActionWrapper from '../RecipientActionWrapper';
import { getGiftHashId } from '../../store/data/data.selectors';
import { meetingSlotsRequest, changeWeek } from '../../store/meeting/meeting.actions';
import { fetchBookedMeetingInfo } from '../../store/bookedMeetingInfo/bookedMeetingInfo.actions';
import {
  setTimezone,
  setCalendarPeriod,
  setMeetingSlots,
  setMeetingNotes,
} from '../../store/giftFlow/giftFlow.actions';
import { getIsPending, getSlots, getWeek } from '../../store/meeting/meeting.selectors';
import { DATE_FORMAT } from '../../../../constants/dates.constants';
import {
  getTimezone,
  getMeetingSlots,
  getFromDate,
  getToDate,
  getMeetingNotes,
} from '../../store/giftFlow/giftFlow.selectors';
import PageTitle from '../PageTitle/PageTitle';
import { GIFT_FLOW_SCHEDULE_ID } from '../../../../constants/giftFlow.constants';
import useGiftStatuses from '../../hooks/useGiftStatuses';
import usePageLoadEvent from '../../../../hooks/usePageLoadEvent';
import useTrackRecipient from '../../../../hooks/useTrackRecipient';
import { getStatuses } from '../../store/statuses/statuses.selectors';
import useResponsive from '../../../../hooks/useResponsive';
import { getSenderState } from '../../../../store/users/users.selectors';
import {
  getIsIdle,
  getIsFulfilled,
  getIsPending as getIsBookedMeetingPending,
} from '../../store/bookedMeetingInfo/bookedMeetingInfo.selectors';

import Calendar from './Calendar/Calendar';
import MeetingNotesForm from './MeetingNotesForm/MeetingNotesForm';

const CalendarPage = (): JSX.Element => {
  const dispatch = useDispatch();
  const { setActiveStatus, goNextStep } = useGiftStatuses();
  const trackEvent = useTrackRecipient();
  const notesSubmitButton = useRef<HTMLButtonElement>(null);
  const { isMobile } = useResponsive();

  const isPending = useSelector(getIsPending);

  const isBookedMeetingInfoIdle = useSelector(getIsIdle);
  const isBookedMeetingInfoPending = useSelector(getIsBookedMeetingPending);
  const isBookedMeetingInfoFulfilled = useSelector(getIsFulfilled);

  const hashId = useSelector(getGiftHashId);
  const sender = useSelector(getSenderState);
  const slots = useSelector(getSlots);
  const timezoneName = useSelector(getTimezone);
  const week = useSelector(getWeek);
  const { hasIntegration } = useSelector(getStatuses);

  const flowFrom = useSelector(getFromDate);
  const flowTo = useSelector(getToDate);
  const flowSelectedSlots = useSelector(getMeetingSlots);
  const flowNotes = useSelector(getMeetingNotes);

  const [selectedSlots, setSelectedSlots] = useState(flowSelectedSlots || []);

  const isLoading = isPending || isBookedMeetingInfoPending;

  const scrollToSubmitButton = useCallback(() => {
    const { current } = notesSubmitButton;
    if (!isMobile && current) {
      current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isMobile]);

  const handleSelectSlot = useCallback(
    (slot, isSelected) => {
      // Single selection mode
      if (hasIntegration && isSelected) {
        setSelectedSlots([]);
      }
      if (hasIntegration && !isSelected) {
        setSelectedSlots([{ ...slot }]);
        scrollToSubmitButton();
      }

      // Multi selection mode
      if (!hasIntegration && isSelected) {
        setSelectedSlots(
          reject(({ startAt, endAt }) => slot.startAt === startAt && slot.endAt === endAt, selectedSlots),
        );
      }
      if (!hasIntegration && !isSelected) {
        setSelectedSlots([...selectedSlots, { ...slot }]);
      }
    },
    [selectedSlots, hasIntegration, scrollToSubmitButton],
  );

  const handleChangeTimezone = useCallback(
    value => {
      dispatch(setTimezone(value.timezoneName));
      trackEvent('Timezone - Selected');
    },
    [dispatch, trackEvent],
  );

  const handleChangeWeek = useCallback(
    weekPeriod => {
      const newWeek = {
        from: weekPeriod.from.format(DATE_FORMAT),
        to: weekPeriod.to.format(DATE_FORMAT),
      };
      dispatch(changeWeek(newWeek));
      dispatch(meetingSlotsRequest({ hashId }));
      trackEvent('Calendar - Change week', { week: newWeek });
    },
    [trackEvent, dispatch, hashId],
  );

  const handleSubmitMeeting = useCallback(
    notes => {
      dispatch(setCalendarPeriod(week));
      dispatch(setMeetingSlots(selectedSlots));
      dispatch(setMeetingNotes(notes));
      trackEvent('Calendar - Submit meeting', { week, selectedSlots });
      goNextStep();
    },
    [dispatch, trackEvent, selectedSlots, week, goNextStep],
  );

  const handleClearMeeting = useCallback(() => setSelectedSlots([]), []);

  const title = hasIntegration
    ? `Pick a time to chat with ${sender?.firstName}`
    : `Pick some time(s) that work best for you to chat with ${sender?.firstName}`;
  const titleContent = <PageTitle title={title} subTitle="Before you confirm your gift..." />;

  useScrollTop();

  usePageLoadEvent('Schedule meeting page - Opened');

  useEffect(() => {
    if (flowTo && flowFrom) {
      dispatch(changeWeek({ from: flowFrom, to: flowTo }));
    }
    if (hashId) {
      dispatch(meetingSlotsRequest({ hashId }));
    }
  }, [dispatch, hashId, flowTo, flowFrom]);

  useEffect(() => {
    setActiveStatus(GIFT_FLOW_SCHEDULE_ID);
  }, [dispatch, setActiveStatus]);

  useEffect(() => {
    if (isBookedMeetingInfoIdle && !flowSelectedSlots) {
      dispatch(fetchBookedMeetingInfo.pending(hashId));
    }
  }, [dispatch, hashId, isBookedMeetingInfoIdle, flowSelectedSlots]);

  useEffect(() => {
    if (isBookedMeetingInfoFulfilled && flowSelectedSlots) {
      setSelectedSlots(flowSelectedSlots);
    }
  }, [isBookedMeetingInfoFulfilled, flowSelectedSlots]);

  return (
    <RecipientActionWrapper titleContent={titleContent} childrenWrapperProps={{ flexDirection: 'column' }}>
      <Calendar
        timezoneName={timezoneName}
        slots={slots}
        selectedSlots={selectedSlots}
        from={week.from}
        to={week.to}
        isLoading={isLoading}
        onChangeWeek={handleChangeWeek}
        onChangeTimezone={handleChangeTimezone}
        onSelectSlot={handleSelectSlot}
      />
      <MeetingNotesForm
        ref={notesSubmitButton}
        notes={flowNotes}
        isLoading={isLoading}
        selectedSlotsCount={selectedSlots.length}
        onSubmit={handleSubmitMeeting}
        onClear={handleClearMeeting}
      />
    </RecipientActionWrapper>
  );
};

export default CalendarPage;
